import { useQuery } from '@tanstack/react-query';
import { Building2, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeCompany, fetchCadeCompanySearch } from '@/services/cade';
import type { CadeCompanyCheck, CadeRegistryCard } from '@/services/types';

/** The refusal that is about the corpus (the link tables are not loaded here). */
const NO_TABLES = 'substrate.reasons.cadeCompanyNoTables';

/** The Receita's registration-status codes; an API answer arrives as a word and is shown as is. */
const STATUS_CODES = new Set(['01', '02', '03', '04', '08']);

/**
 * The antitrust check of a company: a name or CNPJ in, everything CADE's record links to it out.
 *
 * A company is its 8-digit CNPJ root, so its head office, branches and every spelling CADE used
 * are one subject. The ETL linked names to a CNPJ only on evidence, and every link shows how.
 * A decision's outcome is the decision's -- it can treat several parties differently -- and the
 * screen labels it so. Fines are listed with their quotes and never summed. People are never
 * linked, so a person's name finds nothing. Absence here is not clearance.
 */
export function CadeCompanyPage() {
  const { root } = useParams();
  return root ? <CompanyCheckView root={root} /> : <CompanySearch />;
}

function CompanySearch() {
  const { t } = useTranslation(['cade', 'common']);
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [draft, setDraft] = useState(query);
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'company', 'search', query],
    queryFn: () => fetchCadeCompanySearch(query),
    enabled: query.length > 0,
  });
  const number = new Intl.NumberFormat();

  const only = data?.kind === 'root' && data.candidates.length === 1 ? data.candidates[0].root : null;
  useEffect(() => {
    // A CNPJ names one company: go straight to its check.
    if (only) void navigate(`/cade/company/${only}`, { replace: true });
  }, [only, navigate]);

  return (
    <div className="space-y-4 p-6">
      <Header />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}
      <form
        role="search"
        className="flex flex-wrap items-end gap-2 rounded-md border p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setParams(draft.trim() ? { q: draft.trim() } : {}, { replace: true });
        }}
      >
        <label className="min-w-64 flex-1 space-y-1 text-xs text-muted-foreground">
          {t('cade:company.query')}
          <input
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cade:company.placeholder')}
            className="block w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </label>
        <button type="submit" className="rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground">
          {t('cade:company.search')}
        </button>
      </form>
      <p className="text-xs text-muted-foreground">{t('cade:company.scopeNote')}</p>

      {!query ? null : isPending ? (
        <SkeletonLoader rows={4} />
      ) : error ? (
        <ErrorMessage onRetry={() => void refetch()} />
      ) : data.kind === 'invalid_cnpj' ? (
        <p role="alert" className="rounded-md border border-amber-500/50 p-3 text-sm text-foreground">
          {t('cade:company.invalidCnpj')}
        </p>
      ) : data.kind === 'no_tables' ? (
        <p role="alert" className="rounded-md border p-3 text-sm text-foreground">
          {t('common:substrate.reasons.cadeCompanyNoTables')}
        </p>
      ) : data.candidates.length === 0 ? (
        <p className="rounded-md border p-3 text-sm text-foreground" data-company-none>
          {t('cade:company.none')}
        </p>
      ) : (
        <ul className="divide-y rounded-md border" data-company-candidates>
          {data.candidates.map((c) => (
            <li key={c.root} className="flex flex-wrap items-baseline justify-between gap-2 p-3 text-sm">
              <Link to={`/cade/company/${c.root}`} className="font-medium text-primary underline-offset-4 hover:underline">
                {c.legal_name ?? c.root_formatted}
              </Link>
              <span className="text-xs text-muted-foreground">
                {t('cade:company.candidateCounts', {
                  root: c.root_formatted,
                  archive: number.format(c.archive_names),
                  sei: number.format(c.interested_processes),
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Header() {
  const { t } = useTranslation(['cade']);
  return (
    <header className="space-y-1">
      <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
        <Building2 aria-hidden="true" className="size-5" />
        {t('cade:company.title')}
      </h1>
      <p className="text-sm text-muted-foreground">{t('cade:company.subtitle')}</p>
    </header>
  );
}

function CompanyCheckView({ root }: { root: string }) {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'company', root],
    queryFn: () => fetchCadeCompany(root),
  });
  if (isPending) return <div className="p-6"><SkeletonLoader rows={6} /></div>;
  if (error) return <div className="p-6"><ErrorMessage onRetry={() => void refetch()} /></div>;
  const scope = data.check.reason_key === NO_TABLES ? 'corpus' : 'record';
  return (
    <div className="space-y-4 p-6">
      <Header />
      <AsOfStrip asOf={data.as_of} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/cade/company" className="text-sm text-primary underline-offset-4 hover:underline">
          {t('cade:company.back')}
        </Link>
        <ExportButton path={`/api/v1/cade/export/company/${encodeURIComponent(root)}`} />
      </div>
      <Answer answer={data.check} scope={scope} help={t('cade:company.help')}>
        {(check: CadeCompanyCheck) => <CheckBody check={check} />}
      </Answer>
    </div>
  );
}

function CheckBody({ check }: { check: CadeCompanyCheck }) {
  const { t, i18n } = useTranslation(['cade']);
  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  const date = (iso: string | null) => (iso ? day.format(new Date(`${iso.slice(0, 10)}T00:00:00Z`)) : '—');
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-5">
      {check.registry.length > 0 ? <Registry card={check.registry[0]} branches={check.registry.length - 1} /> : null}

      <p className="text-sm text-foreground" data-company-summary>
        {t('cade:company.summary', {
          cases: number.format(check.archive_cases),
          decisions: number.format(check.appearances.length),
          sei: number.format(check.interested.length),
        })}
      </p>

      <section className="space-y-2" data-company-archive>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:company.archive.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('cade:company.archive.note')}</p>
        {check.appearances.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('cade:company.archive.none')}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-muted text-xs text-foreground">
              <tr>
                <th className="p-2">{t('cade:company.archive.case')}</th>
                <th className="p-2">{t('cade:company.archive.date')}</th>
                <th className="p-2">{t('cade:company.archive.name')}</th>
                <th className="p-2">{t('cade:company.archive.role')}</th>
                <th className="p-2">{t('cade:company.archive.outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {check.appearances.map((a, i) => (
                <tr key={`${a.decision_key}-${a.party_name}-${i}`} className="border-t">
                  <td className="p-2">
                    <Link to={`/cade/c/${a.case_key}`} className="tabular-nums text-primary underline-offset-4 hover:underline">
                      {a.case_number}
                    </Link>
                    {a.item_number ? <span className="ml-1 text-xs text-muted-foreground">#{a.item_number}</span> : null}
                  </td>
                  <td className="p-2 tabular-nums">{a.session_date ? date(a.session_date) : (a.session_year ?? '—')}</td>
                  <td className="p-2">{a.party_name}</td>
                  <td className="p-2">{a.role ? t(`cade:parties.roles.${a.role}`, a.role) : '—'}</td>
                  <td className="p-2">
                    {a.outcome_codes.length ? a.outcome_codes.map((c) => t(`cade:overview.outcomes.${c}`, c)).join('; ') : '—'}
                    {a.is_final ? <span className="ml-1 rounded border px-1 text-xs">{t('cade:company.archive.final')}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {check.attributions.length > 0 ? (
        <section className="space-y-2" data-company-fines>
          <h2 className="text-sm font-semibold text-foreground">{t('cade:company.fines.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('cade:company.fines.note')}</p>
          <ul className="space-y-2">
            {check.attributions.map((f, i) => (
              <li key={`${f.case_number}-${i}`} className="rounded-md border p-2 text-sm">
                <Link to={`/cade/c/${f.case_key}`} className="tabular-nums text-primary underline-offset-4 hover:underline">
                  {f.case_number}
                </Link>
                <span className="ml-2">{f.party_name}</span>
                <span className="ml-2 font-medium">{f.amount_brl !== null ? currency.format(f.amount_brl) : '—'}</span>
                {f.evidence_span ? (
                  <blockquote className="mt-1 text-xs italic text-muted-foreground">“{f.evidence_span}”</blockquote>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">{t('cade:company.fines.unverified')}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="space-y-2" data-company-sei>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:company.sei.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('cade:company.sei.note')}</p>
        {check.interested.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('cade:company.sei.none')}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-muted text-xs text-foreground">
              <tr>
                <th className="p-2">{t('cade:company.sei.process')}</th>
                <th className="p-2">{t('cade:company.sei.registered')}</th>
                <th className="p-2">{t('cade:company.sei.type')}</th>
                <th className="p-2">{t('cade:company.sei.listedAs')}</th>
              </tr>
            </thead>
            <tbody>
              {check.interested.map((i) => (
                <tr key={`${i.nup}-${i.name_as_listed}`} className="border-t">
                  <td className="p-2 tabular-nums">
                    {i.case_key ? (
                      <Link to={`/cade/c/${i.case_key}`} className="text-primary underline-offset-4 hover:underline">
                        {i.nup}
                      </Link>
                    ) : (
                      i.nup
                    )}
                  </td>
                  <td className="p-2 tabular-nums">{date(i.registered)}</td>
                  <td className="p-2">{i.process_type?.replace(/^Finalístico:\s*/, '') ?? '—'}</td>
                  <td className="p-2">{i.name_as_listed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <details className="rounded-md border p-3" data-company-evidence>
        <summary className="cursor-pointer text-sm font-medium text-foreground">
          {t('cade:company.evidence.title', { count: check.evidence.length })}
        </summary>
        <ul className="mt-2 space-y-2">
          {check.evidence.map((e) => (
            <li key={`${e.name}-${e.cnpj}`} className="text-xs">
              <span className="font-medium text-foreground">{e.name}</span>
              <span className="ml-2 text-muted-foreground">{t(`cade:company.evidence.method.${e.method}`)}</span>
              {e.quote ? (
                <blockquote className="mt-1 italic text-muted-foreground">“{e.quote.replace(/\s+/g, ' ').trim()}”</blockquote>
              ) : null}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function Registry({ card, branches }: { card: CadeRegistryCard; branches: number }) {
  const { t, i18n } = useTranslation(['cade']);
  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  const status =
    card.status && STATUS_CODES.has(card.status) ? t(`cade:company.registry.status.${card.status}`) : card.status;
  return (
    <section className="rounded-lg border bg-card p-4" data-company-registry>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{card.legal_name ?? card.cnpj_formatted}</h2>
        <span className="tabular-nums text-sm text-muted-foreground">{card.cnpj_formatted}</span>
      </div>
      <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {card.trade_name ? <Field label={t('cade:company.registry.tradeName')} value={card.trade_name} /> : null}
        <Field label={t('cade:company.registry.statusLabel')} value={status ?? '—'} />
        <Field label={t('cade:company.registry.cnae')} value={card.cnae_main ?? '—'} />
        {/* The Receita dump carries its own numeric city code, not a name; only an API answer does. */}
        <Field
          label={t('cade:company.registry.uf')}
          value={
            [card.municipality && !/^\d+$/.test(card.municipality) ? card.municipality : null, card.uf]
              .filter(Boolean)
              .join(' / ') || '—'
          }
        />
      </dl>
      <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
        <ExternalLink aria-hidden="true" className="size-3" />
        {t('cade:company.registry.source', {
          source: t(`cade:company.registry.sources.${card.source.startsWith('rf_') ? 'rf' : card.source}`, card.source),
          date: card.fetched_at ? day.format(new Date(card.fetched_at)) : '—',
        })}
        {branches > 0 ? ` · ${t('cade:company.registry.branches', { count: branches })}` : ''}
      </p>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
